/**
 * Verify baseline — turns `cai verify` from a blind gate into a regression detector.
 *
 * `runVerify()` is stateless: a repo started in a red state gets nagged forever,
 * because the Stop hook can't tell a fresh regression from inherited breakage.
 *
 * This module persists a tiny per-step snapshot to `.cai/.cache/.verify-baseline.json`,
 * keyed on the git HEAD. On the next run we diff was/now:
 *   - step was `passed`, is now `failed` → REGRESSION (the hook should re-engage)
 *   - step was already `failed`          → PRE-EXISTING (don't block on it)
 *
 * Same design rules as drift/history.ts: local-only JSON under .cai/.cache,
 * never throws, never writes outside the cache. Deterministic, no AI, no network.
 * Same CAI_NO_TELEMETRY opt-out so a user can keep verify fully stateless.
 */
import { createHash } from "node:crypto";
import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { dirname, join } from "node:path";
import type { VerifyResult, VerifyStep } from "./verify.js";

const CACHE_DIR = join(".cai", ".cache");
const BASELINE_FILE = ".verify-baseline.json";

/** Per-step snapshot — status plus a hash of the failing output so identical failures stay "pre-existing". */
export interface BaselineStep {
  status: VerifyStep["status"];
  /** md5 of the step's failing output, present only for failed steps. */
  outputHash?: string;
}

export interface VerifyBaseline {
  /** git HEAD the snapshot was taken at, or null when the repo has no commits. */
  head: string | null;
  ts: number;
  steps: Record<string, BaselineStep>;
}

export type StepClassification = "regression" | "pre-existing" | "passing" | "new-failure";

function isDisabled(): boolean {
  return Boolean(process.env.CAI_NO_TELEMETRY);
}

function baselinePath(projectRoot: string): string {
  return join(projectRoot, CACHE_DIR, BASELINE_FILE);
}

function hashOutput(output: string | undefined): string | undefined {
  if (!output) return undefined;
  return createHash("md5").update(output).digest("hex").slice(0, 12);
}

/** Read the previous baseline snapshot, or null when none exists / unreadable. */
export function readBaseline(projectRoot: string): VerifyBaseline | null {
  const path = baselinePath(projectRoot);
  if (!existsSync(path)) return null;
  try {
    const parsed = JSON.parse(readFileSync(path, "utf8")) as VerifyBaseline;
    if (!parsed || typeof parsed !== "object" || typeof parsed.steps !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Write a baseline snapshot from a verify result. Never throws. */
export function writeBaseline(projectRoot: string, head: string | null, result: VerifyResult): void {
  if (isDisabled()) return;
  try {
    const path = baselinePath(projectRoot);
    mkdirSync(dirname(path), { recursive: true });

    const steps: Record<string, BaselineStep> = {};
    for (const step of result.steps) {
      steps[step.name] =
        step.status === "failed"
          ? { status: "failed", outputHash: hashOutput(step.output) }
          : { status: step.status };
    }

    const baseline: VerifyBaseline = { head, ts: Date.now(), steps };
    writeFileSync(path, JSON.stringify(baseline, null, 2) + "\n", "utf8");
  } catch {
    // never fail verify itself over the baseline
  }
}

/**
 * Classify a step against a baseline.
 *
 *   passing       — step did not fail this run.
 *   regression    — step failed, and the baseline recorded it passing.
 *   pre-existing   — step failed, and the baseline recorded the same failure (same output hash).
 *   new-failure    — step failed, and we have no comparable baseline (no prior run, different HEAD,
 *                    or the failure output changed). Treated as a regression by the hook — it's new
 *                    information the agent should react to.
 */
export function classifyStep(step: VerifyStep, baseline: VerifyBaseline | null, headMatches: boolean): StepClassification {
  if (step.status !== "failed") return "passing";
  if (!baseline || !headMatches) return "new-failure";

  const prev = baseline.steps[step.name];
  if (!prev) return "new-failure";
  if (prev.status !== "failed") return "regression";

  // Both failed — same output ⇒ genuinely pre-existing; changed output ⇒ new information.
  const nowHash = hashOutput(step.output);
  if (prev.outputHash && nowHash && prev.outputHash !== nowHash) return "new-failure";
  return "pre-existing";
}

export interface VerifyDelta {
  /** Steps the hook should re-engage on: fresh regressions and new failures. */
  blocking: Array<{ step: VerifyStep; classification: "regression" | "new-failure" }>;
  /** Failed steps the baseline already knew about — reported but not blocking. */
  preExisting: VerifyStep[];
}

/**
 * Compare a verify result against the stored baseline.
 * `headMatches` is true when the current HEAD equals the baseline's HEAD — a HEAD
 * change means commits landed, so prior pass/fail state may no longer apply and we
 * treat any failure as new information rather than inherited.
 */
export function computeDelta(result: VerifyResult, baseline: VerifyBaseline | null, headMatches: boolean): VerifyDelta {
  const blocking: VerifyDelta["blocking"] = [];
  const preExisting: VerifyStep[] = [];

  for (const step of result.steps) {
    const klass = classifyStep(step, baseline, headMatches);
    if (klass === "regression" || klass === "new-failure") {
      blocking.push({ step, classification: klass });
    } else if (klass === "pre-existing") {
      preExisting.push(step);
    }
  }

  return { blocking, preExisting };
}
